import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { AuthProvider } from "@/context/auth-context";
import { ArticlesProvider } from "@/context/ArticlesContext";
import { queryClient } from "@/lib/query-client";
import SnackbarProvider from "@/components/providers/SnackbarProvider";
import { checkMaintenanceStatus, redirectToMaintenance } from "@/lib/maintenance";
import "./index.css";
import "./assets/css/custom.css";
import "./assets/css/mobile.css";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";

if (import.meta.env.DEV && typeof document !== "undefined") {
  document.documentElement.classList.add("debug-screens");
}

// Send visitors straight to the blocker page while the backend is in maintenance mode.
if (window.location.pathname !== "/maintenance") {
  checkMaintenanceStatus().then((down) => {
    if (down) redirectToMaintenance();
  });
}

// Create a new router instance
const router = createRouter({ routeTree });

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Render the app
const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <SnackbarProvider>
          <AuthProvider>
            <ArticlesProvider>
              <RouterProvider router={router} />
              <ReactQueryDevtools initialIsOpen={false} />
            </ArticlesProvider>
          </AuthProvider>
        </SnackbarProvider>
      </QueryClientProvider>
    </StrictMode>
  );
}
